import { useState, useEffect, useCallback, useRef } from 'react';
import { io } from 'socket.io-client';
import { useAlert } from './useAlert';

export const useSocket = (sessionId) => {
	const [socket, setSocket] = useState(null);
	const [connected, setConnected] = useState(false);
	const [connectionStatus, setConnectionStatus] = useState('Connecting...');
	const [messages, setMessages] = useState([]);
	const [sessionInfo, setSessionInfo] = useState(null);
	const [groupInfo, setGroupInfo] = useState(null);
	const [participants, setParticipants] = useState([]);
	const [moderatorThinking, setModeratorThinking] = useState(false);
	const [confirmedAudioMessages, setConfirmedAudioMessages] = useState(
		new Set()
	);
	const { showAlert } = useAlert();
	const baseUrl = process.env.REACT_APP_BASE_URL || '/';

	// Idle detection state
	const [isIdle, setIsIdle] = useState(false);
	const idleTimeoutRef = useRef(null);
	const IDLE_TIMEOUT = 600000; // 10 minutes

	// Function to reset idle timer
	const resetIdleTimer = useCallback(() => {
		// Removed noisy log

		if (idleTimeoutRef.current) {
			clearTimeout(idleTimeoutRef.current);
		}

		// Set new timeout
		idleTimeoutRef.current = setTimeout(() => {
			console.log('Idle timeout fired');
			if (socket && connected) {
				console.log('Sending user_idle message');
				socket.emit('user_idle');
				setIsIdle(true);
			}
		}, IDLE_TIMEOUT);
	}, [socket, connected]);

	// Function to handle user becoming active
	const handleUserActive = useCallback(() => {
		if (socket && connected) {
			console.log('Switching from idle to active');
			socket.emit('user_active');
			setIsIdle(false);
		}
	}, [socket, connected]);

	// Set up activity listeners
	useEffect(() => {
		if (!connected) return;

		console.log('Setting up activity listeners');

		const events = [
			'mousedown',
			'mousemove',
			'keypress',
			'touchstart',
			'click',
		];

		const handleActivity = (event) => {
			// Removed noisy log

			// Clear the current timeout
			if (idleTimeoutRef.current) {
				clearTimeout(idleTimeoutRef.current);
			}

			// If user was idle, make them active
			if (isIdle) {
				handleUserActive();
			}

			// Set new timeout
			resetIdleTimer();
		};

		events.forEach((event) => {
			document.addEventListener(event, handleActivity, true);
		});

		// Start the initial timer
		resetIdleTimer();

		return () => {
			console.log('Cleaning up activity listeners');
			events.forEach((event) => {
				document.removeEventListener(event, handleActivity, true);
			});
			if (idleTimeoutRef.current) {
				clearTimeout(idleTimeoutRef.current);
			}
		};
	}, [connected, resetIdleTimer, isIdle, handleUserActive]);

	useEffect(() => {
		if (!sessionId) return;

		try {
			console.log(baseUrl);

			// Use current window location for socket connection, or allow override via env var
			const socketUrl = process.env.REACT_APP_SOCKET_URL || window.location.origin;

			const socketInstance = io(socketUrl, {
				transports: ['websocket'],
				path: '/socket.io/',
				reconnectionAttempts: 5,
				reconnectionDelay: 1000,
				reconnectionDelayMax: 5000,
				query: {
					session_id: sessionId,
				},
			});

			setSocket(socketInstance);

			console.log(socketInstance);

			socketInstance.on('connect', () => {
				setConnected(true);
				setConnectionStatus('Connected');
			});

			socketInstance.on('disconnect', (reason) => {
				setConnected(false);
				setConnectionStatus(
					`Disconnected: ${reason}. Please refresh the page.`
				);
			});

			socketInstance.on('reconnecting', (attemptNumber) => {
				setConnectionStatus(`Reconnecting... (attempt ${attemptNumber})`);
			});

			socketInstance.on('reconnect_failed', () => {
				setConnectionStatus(
					'Failed to reconnect. Please check your connection and refresh the page.'
				);
			});

			socketInstance.on('session_info', (data) => {
				sessionStorage.setItem('session_id', data.session_id);
				sessionStorage.setItem('chat_username', data.username);
				setSessionInfo(data);
			});

			socketInstance.on('confirm_joined', (data) => {
				setGroupInfo(data);
				// Mark current user in participants list or add if not exists
				setParticipants((prev) => {
					const existingIndex = prev.findIndex(
						(p) => p.username === data.username
					);
					if (existingIndex !== -1) {
						// Update existing participant to mark as current user
						const updated = [...prev];
						updated[existingIndex] = {
							...updated[existingIndex],
							isCurrentUser: true,
							presence_status: 'PRESENT',
						};
						return updated;
					} else {
						// Add current user if not in list
						return [
							...prev,
							{
								username: data.username,
								isCurrentUser: true,
								presence_status: 'PRESENT',
							},
						];
					}
				});
			});

			socketInstance.on('current_participants', (data) => {
				// Set the initial participants list with existing users
				const participantsList = data.participants.map((p) => ({
					username: p.username,
					presence_status: p.presence_status,
					isCurrentUser: false, // Will be updated when confirm_joined is received
				}));
				setParticipants(participantsList);
			});

			socketInstance.on('presence_updated', (data) => {
				setParticipants((prev) =>
					prev.map((p) =>
						p.username === data.username
							? { ...p, presence_status: data.presence_status }
							: p
					)
				);
			});

			socketInstance.on('chat_message', (data) => {
				setMessages((prevMessages) => [...prevMessages, data]);
			});

			socketInstance.on('moderator_thinking', (data) => {
				if (data && data.status === 'start') {
					setModeratorThinking(true);
				} else if (data && data.status === 'done') {
					setModeratorThinking(false);
				}
			});

			socketInstance.on('audio_created', (data) => {
				console.log('Audio created:', data);
				// Extract message_id from the audio_url
				try {
					const url = new URL(data.audio_url, window.location.origin);
					const messageId = url.searchParams.get('message_id');
					if (messageId) {
						setConfirmedAudioMessages((prev) => new Set([...prev, messageId]));
					}
				} catch (error) {
					console.error('Error parsing audio_url:', error);
				}
			});

			socketInstance.on('user_joined', (data) => {
				if (data.error) {
					setConnectionStatus(`Error joining: ${data.error}`);
				} else {
					// Add user to participants list
					setParticipants((prev) => {
						const idx = prev.findIndex((p) => p.username === data.username);
						if (idx === -1) {
							return [
								...prev,
								{
									username: data.username,
									isCurrentUser: false,
									presence_status: 'PRESENT',
								},
							];
						}
						const updated = [...prev];
						updated[idx] = {
							...updated[idx],
							presence_status: 'PRESENT',
						};
						return updated;
					});

					const joinMessage = {
						type: 'system',
						message: `${data.username} has joined`,
						timestamp: new Date().toISOString(),
					};
					setMessages((prevMessages) => [...prevMessages, joinMessage]);
				}
			});

			socketInstance.on('user_left', (data) => {
				// Remove user from participants list
				setParticipants((prev) =>
					prev.filter((p) => p.username !== data.username)
				);

				const leaveMessage = {
					type: 'system',
					message: `${data.username} has left`,
					timestamp: new Date().toISOString(),
				};
				setMessages((prevMessages) => [...prevMessages, leaveMessage]);
			});

			socketInstance.on('error', (error) => {
				setConnectionStatus(`Error: ${error.error}`);
			});

			return () => {
				socketInstance.disconnect();
			};
		} catch (error) {
			console.error('Socket initialization error:', error);
			setConnectionStatus(
				'Failed to connect to server. Please check your connection.'
			);
		}
	}, [sessionId, baseUrl, showAlert]);

	const sendMessage = useCallback(
		(message) => {
			if (!socket || !connected) return false;

			socket.emit('chat_message', {
				username: sessionStorage.getItem('chat_username') || 'Anonymous',
				message,
			});

			return true;
		},
		[socket, connected]
	);

	const callModerator = useCallback(() => {
		if (!socket || !connected) return false;

		socket.emit('call_moderator');
		return true;
	}, [socket, connected]);

	const leaveRoom = useCallback(() => {
		if (socket) {
			socket.disconnect();
			setSocket(null);
			setConnected(false);
			setMessages([]);
			setSessionInfo(null);
			setGroupInfo(null);
			setParticipants([]);
			setConfirmedAudioMessages(new Set());
		}
	}, [socket]);

	return {
		socket,
		connected,
		connectionStatus,
		messages,
		sessionInfo,
		groupInfo,
		participants,
		moderatorThinking,
		confirmedAudioMessages,
		isIdle,
		sendMessage,
		callModerator,
		leaveRoom,
		handleUserActive,
	};
};
